import React from "react";

import {Dialog, Transition} from "@headlessui/react";

import {getIsLoading} from "@client/features/scenes";

import {appStyles, zIndexClasses} from "@client/style";

import {Navigation} from "@client/components/Navigation";
import {Scene} from "@client/components/Scene";
import {SimpleScene} from "@client/components/SimpleScene";
import type {
    Locator,
    RouterRenderPropsArgs,
    TransitionFn,
} from "@client/gotterdammerung";
import * as routes from "@client/journeys/entries/routes";
import * as scenes from "@client/journeys/entries/scenes";
import * as exercises from "@client/journeys/exercises";
import * as onboarding from "@client/journeys/onboarding";
import {useSiteContext} from "@client/site/context";
import {useAppSelector} from "@client/store";
import {classNames} from "@reactivated";

function ModalScene({
    view,
    options,
    parent,
    locator,
    tabs,
    actions,
    close,
    isLoading,
    transition,
}: Pick<
    RouterRenderPropsArgs,
    "view" | "options" | "parent" | "locator" | "tabs" | "actions"
> & {
    close: (result: null) => void;
    isLoading: boolean;
    transition: TransitionFn;
}) {
    const backInfo =
        options.back != null && typeof options.back != "string"
            ? options.back
            : parent != null && options.back != null
              ? {locator: parent, title: options.back}
              : null;

    let back = null;

    if (backInfo != null) {
        if ("onClick" in backInfo) {
            back = {
                title: backInfo.title,
                onClick: backInfo.onClick,
            };
        } else {
            const backLocator = backInfo.locator;

            if ("linkFromAppToSite" in backLocator) {
                throw new Error("Unsupported locator for back");
            }
            back = {
                title: backInfo.title,
                onClick: () => {
                    void transition(backLocator);
                },
            };
        }
    } else if (typeof options.back == "string") {
        back = {
            title: options.back,
            onClick: () => {
                close(null);
            },
        };
    }

    return (
        <>
            <div className={appStyles.navigationAndManager}>
                <Navigation
                    toggleMenu={() => {}}
                    isMenuOpen={false}
                    // isLoading={isLoading || isResolving}
                    isLoading={isLoading}
                    highlight={false}
                    title={options.title}
                    more={options.more}
                    back={back}
                    modal
                >
                    {options.titleSection}
                </Navigation>
            </div>
            <Scene
                modal
                noBottomBorder={options.scene?.kind == "NO_BOTTOM_BORDER"}
                actions={actions}
                tabs={tabs.map((tab) =>
                    tab.icon == false || typeof tab.icon != "string"
                        ? null
                        : {
                              ...tab,
                              icon: tab.icon,
                              isSelected: tab.isActive,
                              link: tab.locator,
                          },
                )}
                topSection={options.topSection}
                bottomSection={options.bottomSection}
                actionsStyle={options.actionsStyle}
                more={options.more}
                locator={locator}
            >
                {view}
            </Scene>
        </>
    );
}

export function SceneModal(props: {
    isOpen: boolean;
    setOpen: (state: boolean) => void;
    children: React.ReactNode;
}) {
    const {realm} = useSiteContext();

    return (
        <Transition.Root show={props.isOpen} as={React.Fragment}>
            {realm == "site" && (
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className={`
                            fixed inset-0 bg-gray-500/75 transition-opacity
                            max-sm:hidden
                            ${zIndexClasses.sceneModalBackdrop}
                        `}
                    />
                </Transition.Child>
            )}
            {/* z-index above the left hand menu on the manager side */}

            <Dialog
                as="div"
                className={`
                    relative
                    ${zIndexClasses.sceneModal}
                `}
                onClose={() => {
                    const targetElement = event?.target as Element | undefined;
                    if (targetElement?.closest(".joy-notification")) {
                        return;
                    }
                    props.setOpen(false);
                }}
            >
                <div
                    style={{maxWidth: 900}}
                    className={classNames(
                        "fixed mx-auto",
                        realm == "site"
                            ? "inset-0 sm:inset-4 sm:overflow-hidden sm:rounded-xl"
                            : "inset-0",
                    )}
                >
                    <Transition.Child
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <Dialog.Panel className="flex min-h-full flex-col bg-white">
                            {props.children}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export function OnboardingJourney(
    props: onboarding.Input & {close: (result: onboarding.Output) => void},
) {
    const isLoading = useAppSelector(getIsLoading);

    return (
        <onboarding.InputContext.Provider value={props}>
            <onboarding.Hooks>
                <onboarding.Route pathname="/">
                    {({view, options, parent, locator, tabs, actions}) => (
                        <ModalScene
                            view={view}
                            options={options}
                            parent={parent}
                            locator={locator}
                            tabs={tabs}
                            actions={actions}
                            close={props.close}
                            isLoading={isLoading}
                            transition={onboarding.router.transition}
                        />
                    )}
                </onboarding.Route>
            </onboarding.Hooks>
        </onboarding.InputContext.Provider>
    );
}

export function ExercisesJourney(
    props: exercises.Input & {close: (result: exercises.Output) => void},
) {
    const isLoading = useAppSelector(getIsLoading);

    return (
        <exercises.InputContext.Provider value={props}>
            <exercises.Hooks>
                <exercises.Route pathname="/">
                    {({view, options, parent, locator, tabs, actions}) => (
                        <ModalScene
                            view={view}
                            options={options}
                            parent={parent}
                            locator={locator}
                            tabs={tabs}
                            actions={actions}
                            close={props.close}
                            isLoading={isLoading}
                            transition={exercises.router.transition}
                        />
                    )}
                </exercises.Route>
            </exercises.Hooks>
        </exercises.InputContext.Provider>
    );
}

// Private factory - NOT exported to maintain HMR boundary
type AnyRouter = {
    _input: unknown;
    _output: unknown;
    _implementations: unknown;
    _routes: Array<{definition: {name: string; tabs: string[]}}>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mount: (implementations: never) => {Route: React.ComponentType<any>};
    transition: TransitionFn;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    locator: (...args: any[]) => Locator;
};

type RouteAndTab<TRouter extends AnyRouter> = {
    [K in keyof TRouter["_routes"] & number]: TRouter["_routes"][K] extends {
        definition: {
            name: infer TName extends string;
            tabs: infer TTabs extends string[];
        };
    }
        ? `${TName}.${TTabs[number]}`
        : never;
}[keyof TRouter["_routes"] & number];

type JourneyProps<TRouter extends AnyRouter> = TRouter["_input"] & {
    route?: RouteAndTab<TRouter>;
    close: (result: TRouter["_output"] | null) => void;
};

function createJourney<TRouter extends AnyRouter>(
    router: TRouter,
    implementations: TRouter["_implementations"],
) {
    const {Route} = router.mount(implementations as never);
    const {name, tabs} = router._routes[0].definition;
    const defaultRoute = `${name}.${tabs[0]}`;
    return function Journey(props: JourneyProps<TRouter>) {
        const {close, route, ...input} = props;
        const isLoading = useAppSelector(getIsLoading);
        const pathname = router.locator({
            route: route ?? defaultRoute,
            params: {},
        }).url;

        return (
            <Route pathname={pathname} input={input} close={close}>
                {({
                    view,
                    options,
                    parent,
                    locator,
                    tabs,
                    actions,
                }: RouterRenderPropsArgs) => (
                    <SimpleScene
                        view={view}
                        options={options}
                        parent={parent}
                        locator={locator}
                        tabs={tabs}
                        actions={actions}
                        close={close}
                        isLoading={isLoading}
                        transition={router.transition}
                    />
                )}
            </Route>
        );
    };
}

export const EntryJourney = createJourney(routes.router, {
    search: scenes.Search,
    create: scenes.Create,
    create_food: scenes.CreateFood,
    barcode_found_confirm_entry: scenes.BarcodeFoundConfirmEntry,
    barcode_found_make_corrections: scenes.BarcodeFoundMakeCorrections,
    barcode_not_found_create_food: scenes.BarcodeNotFoundCreateFood,
    barcode_not_found_confirm_entry: scenes.BarcodeNotFoundConfirmEntry,
});
